import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Flame, Lock, Sparkles, Backpack, Baby } from "lucide-react";
import { CATEGORIES, HOME_BANNER, getItems, LEVEL_SIZE } from "@/data/content";
import { BIGKIDS_CATEGORIES, getBKItems } from "@/data/bigkids";
import { checkin, setMode } from "@/lib/api";
import ProgressModal from "@/components/ProgressModal";
import UpgradeModal from "@/components/UpgradeModal";
import GrownUpGate from "@/components/GrownUpGate";

export default function HomePage({ profile, onSwitch, refreshProfile }) {
  const navigate = useNavigate();
  const [showProgress, setShowProgress] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [gateFor, setGateFor] = useState(null);

  const onGateUnlock = () => {
    const target = gateFor;
    setGateFor(null);
    if (target === "progress") openProgress();
    else if (target === "upgrade") setShowUpgrade(true);
  };

  useEffect(() => {
    checkin(profile.id)
      .then((p) => refreshProfile(p.id))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bigKids = !!profile.big_kids;
  const cats = bigKids ? BIGKIDS_CATEGORIES : CATEGORIES;
  const itemsFor = (key, level) => (bigKids ? getBKItems(key, level) : getItems(key, level));

  const levelOf = (key) => profile.levels?.[key] || 1;
  const learnedInLevel = (key) => {
    const keys = itemsFor(key, levelOf(key)).map((i) => i.k);
    return (profile.progress?.[key] || []).filter((k) => keys.includes(k)).length;
  };

  const badges = cats.filter((c) => learnedInLevel(c.key) >= LEVEL_SIZE);

  const toggleMode = async () => {
    try {
      const p = await setMode(profile.id, !bigKids);
      await refreshProfile(p.id);
    } catch (e) {}
  };

  const openProgress = async () => {
    await refreshProfile(profile.id);
    setShowProgress(true);
  };

  return (
    <div className="app-shell dotted-bg px-6 py-10">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-center mb-6" data-testid="home-brand">
          <img src="/logo.png" alt="TinySpark Learning logo" className="h-[74px] sm:h-[92px] w-auto rounded-2xl shadow-md border-2 border-white" />
        </div>

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-3xl border-4 border-white"
              style={{ backgroundColor: `${profile.color}22`, boxShadow: `0 4px 0 0 ${profile.color}` }}
              data-testid="home-avatar"
            >
              {profile.avatar}
            </div>
            <div>
              <p className="text-sm font-bold uppercase tracking-widest text-slate-400">
                Hi there
              </p>
              <h1 className="font-fredoka text-3xl font-bold text-slate-900 leading-none">
                {profile.name}!
              </h1>
            </div>
            {profile.streak > 0 && (
              <div
                className="flex items-center gap-1.5 bg-orange-100 text-orange-600 font-bold px-3 py-2 rounded-full border-2 border-orange-200"
                data-testid="streak-chip"
                title="Day streak"
              >
                <Flame size={20} strokeWidth={3} className="fill-orange-400 text-orange-500" />
                <span className="text-lg">{profile.streak}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {profile.premium ? (
              <span
                data-testid="premium-badge"
                className="flex items-center gap-1.5 bg-yellow-100 text-yellow-700 font-fredoka font-bold px-3 py-2 rounded-full border-2 border-yellow-300"
              >
                <Sparkles size={18} strokeWidth={3} className="text-yellow-500" /> Premium
              </span>
            ) : (
              <button
                data-testid="unlock-premium-button"
                onClick={() => setGateFor("upgrade")}
                className="toy-btn flex items-center gap-2 bg-yellow-400 text-slate-900 font-fredoka text-lg font-bold px-4 py-2.5 rounded-full border-2 border-white"
                style={{ boxShadow: "0 5px 0 0 rgba(202,138,4,1)" }}
              >
                <Sparkles size={20} strokeWidth={3} /> Unlock Premium
              </button>
            )}
            <button
              data-testid="parent-progress-button"
              onClick={() => setGateFor("progress")}
              className="toy-btn w-14 h-14 rounded-2xl bg-yellow-400 border-4 border-white flex items-center justify-center"
              style={{ boxShadow: "0 5px 0 0 rgba(202,138,4,1)" }}
              title="Parent area"
            >
              <span className="text-2xl">⭐</span>
            </button>
            <button
              data-testid="switch-profile-button"
              onClick={onSwitch}
              className="toy-btn w-14 h-14 rounded-2xl bg-white border-4 border-slate-900/10 flex items-center justify-center"
              style={{ boxShadow: "0 5px 0 0 rgba(15,23,42,0.1)" }}
              title="Switch kid"
            >
              <Users size={26} strokeWidth={3} className="text-slate-600" />
            </button>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-[2rem] overflow-hidden border-4 border-white mb-8 h-32 sm:h-40"
          style={{ boxShadow: "0 8px 0 0 rgba(37,99,235,0.3)" }}
          data-testid="home-banner"
        >
          <img
            src={HOME_BANNER}
            alt="Happy children learning together"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/85 via-blue-500/45 to-transparent" />
          <div className="relative z-10 h-full flex flex-col justify-center px-7">
            <p className="text-sm font-bold uppercase tracking-widest text-white/80">
              Ready to explore?
            </p>
            <p className="font-fredoka text-2xl sm:text-3xl font-bold text-white leading-tight">
              What do you want to learn today?
            </p>
          </div>
        </motion.div>

        <div className="flex flex-col items-center gap-2 mb-6" data-testid="mode-switch">
          <div className="flex items-center justify-center gap-2">
            <button
              data-testid="mode-little"
              onClick={() => bigKids && toggleMode()}
              className={`toy-btn flex flex-col items-center leading-tight font-fredoka font-bold px-5 py-2.5 rounded-2xl border-2 ${
                !bigKids ? "bg-blue-500 text-white border-white" : "bg-white text-slate-500 border-slate-200"
              }`}
              style={!bigKids ? { boxShadow: "0 5px 0 0 rgba(37,99,235,1)" } : {}}
            >
              <span className="flex items-center gap-2 text-lg">
                <Baby size={20} strokeWidth={3} /> Little Kids
              </span>
              <span className={`text-xs ${!bigKids ? "text-white/80" : "text-slate-400"}`}>
                Preschool · Ages 3–5
              </span>
            </button>
            <button
              data-testid="mode-big"
              onClick={() => !bigKids && toggleMode()}
              className={`toy-btn flex flex-col items-center leading-tight font-fredoka font-bold px-5 py-2.5 rounded-2xl border-2 ${
                bigKids ? "bg-pink-500 text-white border-white" : "bg-white text-slate-500 border-slate-200"
              }`}
              style={bigKids ? { boxShadow: "0 5px 0 0 rgba(190,24,93,1)" } : {}}
            >
              <span className="flex items-center gap-2 text-lg">
                <Backpack size={20} strokeWidth={3} /> Big Kids
              </span>
              <span className={`text-xs ${bigKids ? "text-white/80" : "text-slate-400"}`}>
                Kindergarten · 1st Grade
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 sm:gap-8" data-testid="category-grid">
          {cats.map((c, i) => {
            const learned = learnedInLevel(c.key);
            const lvl = levelOf(c.key);
            const locked = c.premium && !profile.premium;
            return (
              <motion.button
                key={c.key}
                data-testid={`category-${c.key}`}
                onClick={() => (locked ? setShowUpgrade(true) : navigate(`/learn/${c.key}`))}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.08, type: "spring", stiffness: 180 }}
                whileHover={{ y: -8 }}
                className="toy-btn relative rounded-[2rem] border-4 border-white/40 p-8 aspect-square flex flex-col items-center justify-center gap-4 overflow-hidden"
                style={{ backgroundColor: c.color, boxShadow: `0 10px 0 0 ${c.shadow}` }}
              >
                {c.cover && (
                  <img
                    src={c.cover}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-luminosity"
                  />
                )}
                <div
                  className="absolute inset-0"
                  style={{ backgroundColor: c.color, opacity: locked ? 0.75 : 0.55 }}
                />
                <span className={`relative z-10 text-7xl sm:text-8xl drop-shadow-lg ${locked ? "opacity-70" : ""}`}>{c.emoji}</span>
                <span className="relative z-10 font-fredoka text-3xl sm:text-4xl font-bold text-white drop-shadow text-center leading-tight">
                  {c.title}
                </span>
                {locked ? (
                  <span
                    data-testid={`lock-${c.key}`}
                    className="absolute z-10 top-4 right-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow"
                  >
                    <Lock size={20} strokeWidth={3} className="text-slate-700" />
                  </span>
                ) : (
                  <span className="absolute z-10 top-4 right-4 flex items-center gap-1">
                    {lvl > 1 && (
                      <span className="bg-white text-slate-800 px-2 py-1 rounded-full font-bold text-xs">
                        Lv{lvl}
                      </span>
                    )}
                    <span className="bg-white/25 backdrop-blur px-3 py-1 rounded-full text-white font-bold text-sm">
                      {learned}/{LEVEL_SIZE}
                    </span>
                  </span>
                )}
              </motion.button>
            );
          })}
        </div>

        <div className="mt-8" data-testid="badges-shelf">
          <p className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-3">
            My Badges
          </p>
          {badges.length === 0 ? (
            <p className="text-lg font-bold text-slate-400">
              Finish a whole category to earn a shiny star! ⭐
            </p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {badges.map((c) => (
                <motion.div
                  key={c.key}
                  data-testid={`badge-${c.key}`}
                  initial={{ scale: 0, rotate: -20 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 260, damping: 14 }}
                  className="flex items-center gap-2 text-white font-fredoka font-bold px-4 py-2.5 rounded-full border-4 border-white"
                  style={{ backgroundColor: c.color, boxShadow: `0 5px 0 0 ${c.shadow}` }}
                >
                  <span className="text-xl">⭐</span>
                  <span>{c.title} Star</span>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ProgressModal
        open={showProgress}
        onClose={() => setShowProgress(false)}
        profile={profile}
        refreshProfile={refreshProfile}
      />
      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        profile={profile}
      />
      <GrownUpGate
        open={!!gateFor}
        onClose={() => setGateFor(null)}
        onUnlock={onGateUnlock}
      />
    </div>
  );
}
